import type { CheckMatrix as CheckMatrixShape, CheckResult } from '@pf/contracts';
import { Check, HelpCircle, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Tooltip } from '@/components/ui/primitives';

/**
 * The SAD §6 four-way check matrix: name · amount · bank · duplicate.
 *
 * This is the component that answers "why was this not auto-approved?", so it does more than
 * show four ticks:
 *
 * - **ABSENT is not FAIL.** The SAD is explicit that the receiving account is "often masked or
 *   omitted → match last-4 when present, don't require". Collapsing absent into failed would
 *   make the console lie about what the provider actually returned, so the three outcomes get
 *   three distinct treatments.
 * - **Each check explains its rule.** Hovering `name` says what threshold it had to clear;
 *   hovering `duplicate` says it is the global `trans_ref` dedupe. The rules are in the SAD;
 *   an operator should not have to be.
 * - **Auto-approval is stated, not inferred.** A footer says in words whether this matrix would
 *   have auto-approved, because "all four are green" and "this passes" are different claims and
 *   the second is the one that matters.
 */

/** Props for {@link CheckMatrix}. */
export interface CheckMatrixProps {
  /** The four checks as the contract carries them. */
  checks: CheckMatrixShape;
  /** Fuzzy name score, shown alongside the name check when known. */
  nameScore?: number | null | undefined;
  /** Auto-approve threshold from policy (SAD §6 default 0.90). */
  nameThreshold?: number | undefined;
  /** Compact rendering. */
  dense?: boolean | undefined;
  /** Additional classes. */
  className?: string | undefined;
}

const RESULT_STYLE: Record<CheckResult, { chip: string; icon: typeof Check; labelKey: string }> = {
  PASS: {
    chip: 'bg-state-good-bg text-state-good ring-1 ring-inset ring-state-good/25',
    icon: Check,
    labelKey: 'domain.checkMatrix.results.pass',
  },
  FAIL: {
    chip: 'bg-state-bad-bg text-state-bad ring-1 ring-inset ring-state-bad/25',
    icon: X,
    labelKey: 'domain.checkMatrix.results.fail',
  },
  ABSENT: {
    chip: 'bg-state-idle-bg text-state-idle ring-1 ring-inset ring-state-idle/25',
    icon: HelpCircle,
    labelKey: 'domain.checkMatrix.results.absent',
  },
};

const CHECK_RULES: Record<keyof CheckMatrixShape, { titleKey: string; ruleKey: string }> = {
  name: {
    titleKey: 'domain.checkMatrix.checks.name.title',
    ruleKey: 'domain.checkMatrix.checks.name.rule',
  },
  amount: {
    titleKey: 'domain.checkMatrix.checks.amount.title',
    ruleKey: 'domain.checkMatrix.checks.amount.rule',
  },
  bank: {
    titleKey: 'domain.checkMatrix.checks.bank.title',
    ruleKey: 'domain.checkMatrix.checks.bank.rule',
  },
  duplicate: {
    titleKey: 'domain.checkMatrix.checks.duplicate.title',
    ruleKey: 'domain.checkMatrix.checks.duplicate.rule',
  },
  /**
   * Present only when the reservation carried a `payer_account` (verify-the-sender) — absent
   * reservations never put this key on the matrix at all, so it never renders for them.
   */
  sender: {
    titleKey: 'domain.checkMatrix.checks.sender.title',
    ruleKey: 'domain.checkMatrix.checks.sender.rule',
  },
  // Published on the wire alongside sender so a participant's UI can explain reviews; the
  // engine always computed them (SAD §6's fifth and sixth rows).
  account: {
    titleKey: 'domain.checkMatrix.checks.account.title',
    ruleKey: 'domain.checkMatrix.checks.account.rule',
  },
  freshness: {
    titleKey: 'domain.checkMatrix.checks.freshness.title',
    ruleKey: 'domain.checkMatrix.checks.freshness.rule',
  },
};

/** Checks that must all be `PASS` for the matrix to satisfy the SAD §6 auto-approve condition. */
const AUTO_APPROVE_KEYS: ReadonlySet<keyof CheckMatrixShape> = new Set([
  'name',
  'amount',
  'bank',
  'duplicate',
  'sender',
]);

/** Render the four-way verification check matrix. */
export function CheckMatrix({
  checks,
  nameScore,
  nameThreshold = 0.9,
  dense = false,
  className,
}: CheckMatrixProps): ReactNode {
  const { t } = useTranslation();
  const entries = Object.entries(checks) as [keyof CheckMatrixShape, CheckResult][];
  const autoApproves = entries.every(([key, result]) =>
    AUTO_APPROVE_KEYS.has(key) ? result === 'PASS' : true,
  );

  return (
    <div className={cn('space-y-3', className)}>
      <ul className={cn('grid gap-2', dense ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-4')}>
        {entries.map(([key, result]) => {
          const style = RESULT_STYLE[result];
          const Icon = style.icon;
          const rule = CHECK_RULES[key];
          // A key this build does not know (a newer API) renders nothing rather than crashing
          // the whole review page — the operator still sees every check it does understand.
          if (!rule || !style) return null;
          return (
            <li key={key}>
              <Tooltip
                content={
                  <span>
                    <span className="font-semibold">{t(rule.titleKey)}</span>
                    <br />
                    {t(rule.ruleKey)}
                  </span>
                }
              >
                <div
                  className={cn(
                    'flex w-full cursor-help flex-col gap-1.5 rounded-lg border border-border/70 bg-card p-3 text-left',
                  )}
                  data-check={key}
                  data-result={result}
                >
                  <span className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t(rule.titleKey)}
                  </span>
                  <span
                    className={cn(
                      'inline-flex w-fit items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold',
                      style.chip,
                    )}
                  >
                    <Icon className="size-3.5" aria-hidden="true" />
                    {t(style.labelKey)}
                  </span>
                  {key === 'name' && nameScore !== null && nameScore !== undefined ? (
                    <span className="tnum text-2xs text-muted-foreground">
                      {t('domain.checkMatrix.scoreLine', {
                        score: nameScore.toFixed(2),
                        threshold: nameThreshold.toFixed(2),
                      })}
                    </span>
                  ) : null}
                </div>
              </Tooltip>
            </li>
          );
        })}
      </ul>

      <p
        className={cn(
          'flex items-center gap-2 rounded-md border px-3 py-2 text-xs',
          autoApproves
            ? 'border-state-good/25 bg-state-good-bg text-state-good'
            : 'border-state-attention/25 bg-state-attention-bg text-state-attention',
        )}
      >
        {autoApproves ? (
          <Check className="size-3.5 shrink-0" aria-hidden="true" />
        ) : (
          <HelpCircle className="size-3.5 shrink-0" aria-hidden="true" />
        )}
        {autoApproves
          ? t('domain.checkMatrix.autoApprovePass')
          : t('domain.checkMatrix.autoApproveFail')}
      </p>
    </div>
  );
}
